import Editor from "@monaco-editor/react";
import "@vscode-elements/elements/dist/vscode-tree/index.js";
import "@vscode-elements/elements/dist/vscode-tree-item/index.js";
import "@vscode/codicons/dist/codicon.css";
import { Download, File } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useCiTestLogs } from "@/hooks/use-ci";
import { api } from "@/lib/api";
import type { CiLogFile } from "@/lib/types";
import { cn } from "@/lib/utils";

interface TreeNode {
  name: string;
  path: string;
  isDirectory: boolean;
  children: TreeNode[];
}

function buildFileTree(files: CiLogFile[]): TreeNode[] {
  const root: TreeNode[] = [];

  for (const file of files) {
    const parts = file.path.split("/");
    let currentLevel = root;

    for (let index = 0; index < parts.length; index++) {
      const part = parts[index] ?? "";
      const isLast = index === parts.length - 1;
      const existing = currentLevel.find((node) => node.name === part);

      if (existing) {
        currentLevel = existing.children;
      } else {
        const newNode: TreeNode = {
          name: part,
          path: isLast ? file.path : parts.slice(0, index + 1).join("/"),
          isDirectory: !isLast,
          children: [],
        };
        currentLevel.push(newNode);
        currentLevel = newNode.children;
      }
    }
  }

  return root;
}

/**
 * Recursively render TreeNode → <vscode-tree-item>.
 *
 * Two quirks of @vscode-elements/elements we work around here:
 *
 * 1. The component's `initPathTrackerProps` helper overwrites
 *    `item.dataset.path` with its own internal path id (e.g. "0.1.2"), so we
 *    store our file path on `data-file-path` instead and read it in the
 *    select-event handler as `dataset.filePath`.
 *
 * 2. The shadow-DOM stylesheet targets `.label ::slotted(*)` — which only
 *    matches element nodes, not bare text. Wrapping the name in a <span>
 *    makes sure it gets the `display:inline-block; max-width:100%` treatment
 *    the component expects (without the wrapper, long filenames don't wrap/
 *    truncate properly and can render blank on narrow sidebars).
 *
 * 3. `_mainSlotChange` moves child <vscode-tree-item> nodes to the "children"
 *    slot via setAttribute, but React re-renders can wipe that. We set
 *    `slot="children"` explicitly on nested items so React owns the attribute.
 */
function renderTreeItems(
  nodes: TreeNode[],
  selectedPath: string | null,
  nested: boolean,
): React.ReactNode {
  return nodes.map((node) => {
    const nestedSlot = nested ? { slot: "children" } : {};
    if (node.isDirectory) {
      return (
        <vscode-tree-item key={node.path} branch open data-file-path={node.path} {...nestedSlot}>
          <span>{node.name}</span>
          {renderTreeItems(node.children, selectedPath, true)}
        </vscode-tree-item>
      );
    }
    const isSelected = selectedPath === node.path;
    return (
      <vscode-tree-item
        key={node.path}
        data-file-path={node.path}
        selected={isSelected || undefined}
        {...nestedSlot}
      >
        <span>{node.name}</span>
      </vscode-tree-item>
    );
  });
}

function detectLanguage(fileName: string): string {
  if (fileName.endsWith(".log")) return "log";
  if (fileName.endsWith(".json")) return "json";
  if (fileName.endsWith(".yaml") || fileName.endsWith(".yml")) return "yaml";
  return "plaintext";
}

export function CiLogViewer({
  runId,
  testName,
  version,
}: {
  runId: string;
  testName: string;
  version: string;
}) {
  const { data: logFiles, isLoading, error } = useCiTestLogs(runId, testName, version);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [logText, setLogText] = useState<string>("");
  const [loadingLog, setLoadingLog] = useState(false);

  const fileTree = useMemo(() => buildFileTree(logFiles ?? []), [logFiles]);

  // Callback ref instead of `useRef` + `useEffect([])`: the LogViewer
  // shows a loading skeleton on the first render and the `<vscode-tree>`
  // element doesn't exist yet, so a one-shot mount effect would never see
  // it. The callback ref fires when the element actually attaches/detaches.
  //
  // Why `vsc-tree-select` (and not click delegation): in
  // `@vscode-elements/elements@2.5.1` every item's `_handleContentClick`
  // calls `ev.stopPropagation()` before the click escapes the item's
  // shadow DOM, so a `click` listener on the tree host never sees item
  // clicks. Quirks of the event itself:
  //   - `detail` is a bare `VscodeTreeItem[]` array (see
  //     `_emitSelectEvent` in dist/vscode-tree/vscode-tree.js), even
  //     though the .d.ts declares `{selectedItems: [...]}` — reading
  //     `event.detail.selectedItems` always returns `undefined`.
  //   - It doesn't bubble or compose, so the listener must be on the
  //     `<vscode-tree>` element directly.
  //   - `data-file-path` not `data-path` — `initPathTrackerProps` rewrites
  //     `data-path` to its internal "0.1.2" path id.
  const treeRef = useCallback((node: HTMLElement | null) => {
    if (!node) return;
    const handler = (event: Event) => {
      const items = (event as CustomEvent<HTMLElement[]>).detail;
      const item = Array.isArray(items) ? items[0] : undefined;
      if (!item) return;
      if (item.hasAttribute("branch")) return;
      const filePath = item.getAttribute("data-file-path");
      if (filePath) setSelectedPath(filePath);
    };
    node.addEventListener("vsc-tree-select", handler);
    return () => node.removeEventListener("vsc-tree-select", handler);
  }, []);

  // Top-level files (no `/` in path) get tabs at the top. Nested files are
  // accessible only via the sidebar tree to avoid an overflowing tab bar on
  // cryod runs that ship 20+ artifacts per test.
  const tabFiles = useMemo(
    () => (logFiles ?? []).filter((file) => !file.path.includes("/")),
    [logFiles],
  );

  const selectedFile = useMemo(
    () => logFiles?.find((file) => file.path === selectedPath) ?? null,
    [logFiles, selectedPath],
  );

  // Auto-select first file
  useEffect(() => {
    if (logFiles && logFiles.length > 0 && !selectedPath) {
      setSelectedPath(logFiles[0]?.path ?? null);
    }
  }, [logFiles, selectedPath]);

  // Fetch log content when selection changes
  useEffect(() => {
    if (!selectedPath) return;
    const controller = new AbortController();
    setLoadingLog(true);
    setLogText("");
    const logUrl = api.getCiTestLogUrl(runId, testName, version, selectedPath);
    fetch(logUrl, { credentials: "include", signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
        return response.text();
      })
      .then((text) => {
        setLogText(text);
        setLoadingLog(false);
      })
      .catch((fetchError) => {
        if (fetchError.name === "AbortError") return;
        setLogText(`Failed to load log file: ${fetchError.message}`);
        setLoadingLog(false);
      });
    return () => controller.abort();
  }, [selectedPath, runId, testName, version]);

  if (isLoading) {
    return <div className="py-4 text-sm text-muted-foreground">Loading log files...</div>;
  }

  if (error) {
    const message = error instanceof Error ? error.message : "unknown error";
    return <div className="py-4 text-sm text-red-400">Failed to load log files: {message}</div>;
  }

  if (!logFiles || logFiles.length === 0) {
    return (
      <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
        <div className="font-medium text-amber-400">Logs unavailable</div>
        <div className="mt-1 text-muted-foreground">
          No artifacts are available for this test. Either the publisher never uploaded them (test
          crashed before its upload step) or fleet can't reach GCS right now (infra issue, check the
          fleet logs for a GCS auth/init failure).
        </div>
      </div>
    );
  }

  const downloadUrl = selectedFile
    ? api.getCiTestLogUrl(runId, testName, version, selectedFile.path)
    : undefined;

  return (
    <div className="overflow-hidden rounded-lg border border-[#3c3c3c] h-[65vh] min-h-80 flex flex-col bg-[#1e1e1e]">
      {/* Tab bar */}
      <div className="flex items-center justify-between border-b border-[#3c3c3c] bg-[#252526] px-1">
        <div className="flex items-center gap-0.5 overflow-x-auto">
          {tabFiles.map((file) => (
            <button
              key={file.path}
              type="button"
              onClick={() => setSelectedPath(file.path)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs whitespace-nowrap border-b-2 transition-colors",
                selectedPath === file.path
                  ? "border-[#007acc] text-[#e0e0e0] bg-[#1e1e1e]"
                  : "border-transparent text-[#969696] hover:text-[#e0e0e0]",
              )}
            >
              <File className="size-3 text-[#519aba]/70" />
              {file.name}
            </button>
          ))}
        </div>
        {downloadUrl && (
          <a
            href={downloadUrl}
            download={selectedFile?.name}
            className="flex items-center gap-1 px-2 py-1 text-xs text-[#969696] hover:text-[#e0e0e0] transition-colors shrink-0"
            aria-label="Download file"
          >
            <Download className="size-3" />
            <span className="hidden sm:inline">Download</span>
          </a>
        )}
      </div>

      {/* Main area: file tree + editor, resizable.
          react-resizable-panels v4 treats NUMERIC sizes as PIXELS. Strings
          without units ("32") and strings with "%" ("32%") are percentages.
          Use percentage strings so the sidebar scales with container width.
          v4 also dropped autoSaveId — persistence now requires wiring
          onLayoutChanged to storage ourselves, which we skip for now. */}
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
        <ResizablePanel
          defaultSize="22%"
          minSize="14%"
          maxSize="55%"
          className="border-r border-[#3c3c3c] bg-[#252526]"
        >
          <div className="h-full overflow-x-hidden overflow-y-auto">
            <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#969696]">
              Explorer
            </div>
            {/* @vscode-elements: vscode-tree handles keyboard nav, indent
                guides, expand/collapse, selection, and matches VS Code's
                visual style (chevron from codicons). We only wire the
                select event → React state, rest is the component's own. */}
            <vscode-tree ref={treeRef} indent-guides="onHover">
              {renderTreeItems(fileTree, selectedPath, false)}
            </vscode-tree>
          </div>
        </ResizablePanel>
        <ResizableHandle className="bg-[#3c3c3c] hover:bg-primary/60 data-[dragging=true]:bg-[#007acc]" />
        <ResizablePanel defaultSize="78%" minSize="40%" className="min-w-0">
          {loadingLog ? (
            <div className="flex h-full items-center justify-center text-xs text-[#969696]">
              Loading...
            </div>
          ) : logText ? (
            <Editor
              height="100%"
              language={detectLanguage(selectedFile?.name ?? "")}
              value={logText}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: true, scale: 2 },
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                wordWrap: "on",
                fontSize: 12,
                fontFamily: "'Geist Mono Variable', 'Fira Code', monospace",
                fontLigatures: true,
                renderWhitespace: "none",
                domReadOnly: true,
                contextmenu: true,
                smoothScrolling: true,
                cursorBlinking: "smooth",
                padding: { top: 8 },
                find: {
                  addExtraSpaceOnTop: true,
                  autoFindInSelection: "never",
                  seedSearchStringFromSelection: "always",
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-xs text-[#969696]">
              Select a file to view
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
